import React, { useState, useCallback, useEffect } from 'react';
import { Upload, Link, FileCode, Loader2, Sparkles, Check, AlertCircle, Eye, Lock, Crown } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import api from '../utils/api';
import { toast } from 'sonner';

const PREMIUM_PLANS = ['pro', 'enterprise'];

const HTMLImportModal = ({ open, onClose, onImported }) => {
  const [activeTab, setActiveTab] = useState('upload');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [planLevel, setPlanLevel] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(true);
  
  // Upload state
  const [htmlContent, setHtmlContent] = useState('');
  const [cssContent, setCssContent] = useState('');
  const [templateName, setTemplateName] = useState('Imported Template');
  
  // URL state
  const [importUrl, setImportUrl] = useState('');
  
  // Drag & drop state
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (open) {
      fetchTenantPlan();
    }
  }, [open]);

  const fetchTenantPlan = async () => {
    try {
      setLoadingPlan(true);
      const res = await api.get('/tenant');
      setPlanLevel(res.data.plan_level || 'starter');
    } catch (error) {
      console.error('Failed to fetch plan:', error);
      setPlanLevel('starter');
    } finally {
      setLoadingPlan(false);
    }
  };

  const isPremiumPlan = PREMIUM_PLANS.includes(planLevel);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  }, []);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    processFiles(files);
  };

  const processFiles = (files) => {
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target.result;
        if (file.name.endsWith('.html') || file.name.endsWith('.htm')) {
          setHtmlContent(content);
          // Extract template name from filename
          const name = file.name.replace(/\.(html|htm)$/, '').replace(/[-_]/g, ' ');
          setTemplateName(name.charAt(0).toUpperCase() + name.slice(1));
        } else if (file.name.endsWith('.css')) {
          setCssContent(prev => prev + '\n' + content);
        }
      };
      reader.readAsText(file);
    });
    toast.success(`Loaded ${files.length} file(s)`);
  };

  const handleImportFromUpload = async () => {
    if (!htmlContent.trim()) {
      toast.error('Please upload or paste HTML content');
      return;
    }

    setImporting(true);
    setResult(null);

    try {
      const res = await api.post('/templates/import-html', {
        html_content: htmlContent,
        css_content: cssContent || null,
        template_name: templateName,
        template_description: `Imported HTML template with ${cssContent ? 'CSS styles' : 'inline styles'}`
      });

      setResult(res.data);
      toast.success(`Template imported with ${res.data.blocks?.length || 0} blocks!`);
      
      if (onImported) {
        onImported(res.data);
      }
    } catch (error) {
      console.error('Import failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to import template');
    } finally {
      setImporting(false);
    }
  };

  const handleImportFromUrl = async () => {
    if (!importUrl.trim()) {
      toast.error('Please enter a URL');
      return;
    }

    if (!importUrl.startsWith('http://') && !importUrl.startsWith('https://')) {
      toast.error('Please enter a valid URL starting with http:// or https://');
      return;
    }

    setImporting(true);
    setResult(null);

    try {
      const res = await api.post(`/templates/import-url?url=${encodeURIComponent(importUrl)}&template_name=${encodeURIComponent(templateName)}`);
      
      setResult(res.data);
      toast.success(`Template imported with ${res.data.blocks?.length || 0} blocks!`);
      
      if (onImported) {
        onImported(res.data);
      }
    } catch (error) {
      console.error('Import failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to import from URL');
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setHtmlContent('');
    setCssContent('');
    setImportUrl('');
    setTemplateName('Imported Template');
    setResult(null);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FileCode className="text-purple-600" size={24} />
            Import HTML Template
            <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-normal">
              Premium Feature
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Loading Plan State */}
        {loadingPlan ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-slate-400" size={32} />
          </div>
        ) : !isPremiumPlan ? (
          /* Plan Gating - Not Premium */
          <div className="flex-1 flex flex-col items-center justify-center py-8 text-center">
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mb-4">
              <Lock className="text-purple-600" size={40} />
            </div>
            <h3 className="text-xl font-semibold text-slate-800 mb-2">Upgrade to Pro</h3>
            <p className="text-slate-600 max-w-md mb-6">
              HTML Import is a premium feature available on Pro and Enterprise plans. 
              Upgrade your plan to convert HTML templates into editable CMS blocks using AI.
            </p>
            <div className="flex flex-col items-center gap-2 mb-6 p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Crown className="text-amber-500" size={16} />
                Your current plan: <span className="font-semibold capitalize">{planLevel}</span>
              </div>
            </div>
            <Button 
              onClick={() => window.location.href = '/settings?tab=billing'}
              className="gap-2 bg-purple-600 hover:bg-purple-700"
            >
              <Crown size={16} />
              Upgrade to Pro
            </Button>
          </div>
        ) : (
          /* Main Import Content - Premium Users */
          <>
            {/* Cost Info */}
            <Alert className="bg-amber-50 border-amber-200">
              <Sparkles className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                HTML import uses <strong>2 AI credits</strong> to analyze and convert your template to editable blocks.
              </AlertDescription>
            </Alert>

            {result ? (
              /* Success State */
              <div className="flex-1 overflow-auto py-4 space-y-4">
                <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
                  <Check className="text-green-600" size={24} />
                  <div>
                    <h3 className="font-semibold text-green-800">Import Successful!</h3>
                    <p className="text-green-700 text-sm">{result.message}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">Blocks Created</p>
                    <p className="text-2xl font-bold text-slate-800">{result.blocks?.length || 0}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">Credits Used</p>
                    <p className="text-2xl font-bold text-slate-800">{result.credits_used}</p>
              </div>
            </div>

            {result.extracted_theme && Object.keys(result.extracted_theme).length > 0 && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-700 mb-2">Extracted Theme</p>
                <div className="flex flex-wrap gap-2">
                  {result.extracted_theme.primaryColor && (
                    <div className="flex items-center gap-2 px-3 py-1 bg-white rounded-full border">
                      <div 
                        className="w-4 h-4 rounded-full border" 
                        style={{ backgroundColor: result.extracted_theme.primaryColor }}
                      />
                      <span className="text-xs">Primary</span>
                    </div>
                  )}
                  {result.extracted_theme.backgroundColor && (
                    <div className="flex items-center gap-2 px-3 py-1 bg-white rounded-full border">
                      <div 
                        className="w-4 h-4 rounded-full border" 
                        style={{ backgroundColor: result.extracted_theme.backgroundColor }}
                      />
                      <span className="text-xs">Background</span>
                    </div>
                  )}
                  {result.extracted_theme.fontFamily && (
                    <div className="px-3 py-1 bg-white rounded-full border text-xs">
                      Font: {result.extracted_theme.fontFamily}
                    </div>
                  )}
                </div>
              </div>
            )}

            {result.unmapped_css && (
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-sm font-medium text-orange-700 mb-2">
                  <AlertCircle className="inline mr-1" size={14} />
                  Some CSS could not be mapped to block styles
                </p>
                <details className="text-xs">
                  <summary className="cursor-pointer text-orange-600">View unmapped CSS</summary>
                  <pre className="mt-2 p-2 bg-white rounded text-slate-600 overflow-auto max-h-32">
                    {result.unmapped_css}
                  </pre>
                </details>
              </div>
            )}

            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={handleReset}>
                Import Another
              </Button>
              <Button 
                onClick={() => window.location.href = `/pages/new?template=${result.template_id}`}
                className="gap-2"
              >
                <Eye size={16} />
                Use Template
              </Button>
            </DialogFooter>
          </div>
        ) : (
          /* Import Form */
          <div className="flex-1 overflow-auto">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="upload" className="gap-2">
                  <Upload size={16} />
                  Upload Files
                </TabsTrigger>
                <TabsTrigger value="url" className="gap-2">
                  <Link size={16} />
                  From URL
                </TabsTrigger>
              </TabsList>

              <TabsContent value="upload" className="space-y-4 mt-4">
                {/* Template Name */}
                <div className="space-y-2">
                  <Label>Template Name</Label>
                  <Input
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="My Imported Template"
                  />
                </div>

                {/* Drag & Drop Zone */}
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                    isDragging 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-slate-300 hover:border-slate-400'
                  }`}
                >
                  <Upload className={`mx-auto mb-3 ${isDragging ? 'text-purple-500' : 'text-slate-400'}`} size={32} />
                  <p className="font-medium text-slate-700">
                    Drag & drop HTML and CSS files here
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    or click to select files
                  </p>
                  <input
                    type="file"
                    multiple
                    accept=".html,.htm,.css"
                    onChange={handleFileSelect}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                    style={{ position: 'relative', marginTop: '12px' }}
                  />
                </div>

                {/* File Status */}
                <div className="flex gap-3">
                  <div className={`flex-1 p-3 rounded-lg border ${htmlContent ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                    <p className="text-sm font-medium flex items-center gap-2">
                      {htmlContent ? <Check size={14} className="text-green-600" /> : null}
                      HTML {htmlContent ? `(${(htmlContent.length / 1024).toFixed(1)} KB)` : '(not loaded)'}
                    </p>
                  </div>
                  <div className={`flex-1 p-3 rounded-lg border ${cssContent ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                    <p className="text-sm font-medium flex items-center gap-2">
                      {cssContent ? <Check size={14} className="text-green-600" /> : null}
                      CSS {cssContent ? `(${(cssContent.length / 1024).toFixed(1)} KB)` : '(optional)'}
                    </p>
                  </div>
                </div>

                {/* Manual Paste */}
                <details className="group">
                  <summary className="cursor-pointer text-sm text-slate-600 hover:text-slate-800">
                    Or paste HTML/CSS manually
                  </summary>
                  <div className="mt-3 space-y-3">
                    <div>
                      <Label className="text-xs">HTML Content</Label>
                      <Textarea
                        value={htmlContent}
                        onChange={(e) => setHtmlContent(e.target.value)}
                        placeholder="<html>...</html>"
                        rows={5}
                        className="font-mono text-xs"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">CSS Content (optional)</Label>
                      <Textarea
                        value={cssContent}
                        onChange={(e) => setCssContent(e.target.value)}
                        placeholder=".class { ... }"
                        rows={3}
                        className="font-mono text-xs"
                      />
                    </div>
                  </div>
                </details>

                <Button 
                  onClick={handleImportFromUpload} 
                  disabled={importing || !htmlContent}
                  className="w-full gap-2"
                >
                  {importing ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      Analyzing & Converting...
                    </>
                  ) : (
                    <>
                      <Sparkles size={18} />
                      Import Template (2 Credits)
                    </>
                  )}
                </Button>
              </TabsContent>

              <TabsContent value="url" className="space-y-4 mt-4">
                {/* Template Name */}
                <div className="space-y-2">
                  <Label>Template Name</Label>
                  <Input
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="My Imported Template"
                  />
                </div>

                {/* URL Input */}
                <div className="space-y-2">
                  <Label>Website URL</Label>
                  <Input
                    value={importUrl}
                    onChange={(e) => setImportUrl(e.target.value)}
                    placeholder="https://example.com/template.html"
                    type="url"
                  />
                  <p className="text-xs text-slate-500">
                    Enter the URL of a webpage to import. We will fetch the HTML and linked CSS automatically.
                  </p>
                </div>

                <Alert className="bg-blue-50 border-blue-200">
                  <AlertDescription className="text-blue-800 text-sm">
                    <strong>Note:</strong> This works best with simple, static HTML pages. 
                    Complex JavaScript-rendered sites may not import correctly.
                  </AlertDescription>
                </Alert>

                <Button 
                  onClick={handleImportFromUrl} 
                  disabled={importing || !importUrl}
                  className="w-full gap-2"
                >
                  {importing ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      Fetching & Converting...
                    </>
                  ) : (
                    <>
                      <Sparkles size={18} />
                      Import from URL (2 Credits)
                    </>
                  )}
                </Button>
              </TabsContent>
            </Tabs>
          </div>
        )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HTMLImportModal;
